import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import ItemCard from "../components/ItemCard";
import { ArrowRight } from "lucide-react";

const CHARACTERS = {
  boggy: {
    name: "Boggy",
    species: "the frog",
    accent: "bg-green",
    accentText: "text-ink",
    voice: "Hey hey! Boggy here.",
    intro: "I'm the loud one. If a piece doesn't make me feel like I'm at a 1986 pool party, it doesn't end up here.",
    bullets: [
      { title: "I love loud", body: "Florals, neons, prints that argue with each other. Yes." },
      { title: "Patterns are personality", body: "A solid tee is a Tuesday. A Hawaiian shirt is a vibe." },
      { title: "If it's not bright, it's not right", body: "Cream and beige can sit this one out. We're picking color." },
    ],
    cta: "Shop Boggy's picks",
    title: "Boggy's Closet · Loud, Bright, 100% Frog-Approved",
    description: "Pre-loved second-hand clothing curated by Boggy the frog — bold patterns, vintage colors, and 80s-cartoon energy. Wild prices, fast shipping, direct from a real small business.",
  },
  fox: {
    name: "Fox",
    species: "the fox",
    accent: "bg-orange",
    accentText: "text-white",
    voice: "Fox here. Let's keep it sharp.",
    intro: "I'm the clean one. I want pieces that work in 1986, in 2026, and in 2056. Quality first, hype never.",
    bullets: [
      { title: "Denim is forever", body: "Levi's, Lees, Wranglers. The right pair gets better every wash." },
      { title: "Basics, but the best ones", body: "Soft tees, real wool, real cotton. No polyester sadness." },
      { title: "Fit > brand", body: "I don't care about the label. I care about the cut, the drape, the line." },
    ],
    cta: "Shop Fox's picks",
    title: "Fox's Picks · Clean Vintage, Real Quality, Real Cheap",
    description: "Pre-loved second-hand clothing curated by Fox the fox — clean denim, perfect basics, and vintage classics that never go out. Hand-picked, real-priced, shipped fast.",
  },
};

export default function CharacterPage() {
  const params = useParams();
  const loc = useLocation();
  const who = params.who || (loc.pathname.includes("boggy") ? "boggy" : loc.pathname.includes("fox") ? "fox" : null);
  const c = who ? CHARACTERS[who] : null;
  const [items, setItems] = useState([]);
  const [mascots, setMascots] = useState({});

  useEffect(() => {
    if (!c) return;
    document.title = c.title;
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.name = "description";
      document.head.appendChild(m);
      return m;
    })();
    meta.content = c.description;
    api.get("/items", { params: { curator: who } }).then((r) => setItems(r.data.slice(0, 12))).catch(() => {});
    api.get("/mascots").then((r) => setMascots(r.data || {})).catch(() => {});
    return () => { document.title = "Boggy and Fox"; };
  }, [who, c]);

  if (!c) return <div className="p-10 font-display text-3xl uppercase">Not found.</div>;

  const portrait = who === "boggy" ? mascots.boggy : mascots.fox;

  return (
    <div data-testid={`character-page-${who}`}>
      {/* Hero */}
      <section className={`${c.accent} ${c.accentText} border-b-4 border-[#1A1A1A]`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <span className="tag-sticker bg-white text-ink text-xl rotate-tilt-l inline-block mb-3">curator</span>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.9]">
              {c.voice}
            </h1>
            <p className="mt-5 text-lg md:text-xl max-w-xl">{c.intro}</p>
            <div className="mt-7 grid sm:grid-cols-3 gap-4">
              {c.bullets.map((b, i) => (
                <div key={i} className="brut bg-paper text-ink p-4">
                  <div className="font-display text-lg uppercase">{b.title}</div>
                  <div className="text-sm mt-1">{b.body}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5 order-1 md:order-2 relative">
            <div className="brut bg-paper p-3 -rotate-2">
              {portrait ? (
                <img src={fileUrl(portrait)} alt={c.name} className="w-full h-auto" />
              ) : (
                <div className="aspect-square bg-yellow flex items-center justify-center font-display text-6xl uppercase">{c.name}</div>
              )}
            </div>
            <span className="tag-sticker bg-yellow text-ink absolute -bottom-4 -left-4 text-2xl rotate-tilt-r">{c.name}</span>
          </div>
        </div>
      </section>

      {/* Picks */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <span className="tag-sticker bg-yellow text-xl rotate-tilt-l inline-block mb-1">{c.name}'s rack</span>
            <h2 className="font-display text-3xl md:text-5xl uppercase">{c.cta}</h2>
          </div>
          <Link to={`/shop?curator=${who}`} className={`brut-btn ${c.accent} ${c.accentText} px-5 py-3 flex items-center gap-2`} data-testid={`character-shop-${who}`}>
            See all <ArrowRight size={18} strokeWidth={3} />
          </Link>
        </div>
        {items.length === 0 ? (
          <div className="brut p-10 text-center">
            <p className="font-display text-2xl uppercase">{c.name} is still picking. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {items.map((it, i) => <ItemCard key={it.id} item={it} idx={i} />)}
          </div>
        )}
      </section>

      <footer className="border-t-4 border-[#1A1A1A] bg-paper py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-wrap gap-3 justify-between items-center text-sm">
          <Link to="/" className="font-display text-xl uppercase">← Back to Boggy <span className="font-sticker text-orange">&</span> Fox</Link>
          <Link to={who === "boggy" ? "/fox" : "/boggy"} className="font-bold underline">Meet {who === "boggy" ? "Fox" : "Boggy"} →</Link>
        </div>
      </footer>
    </div>
  );
}
